// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AssetMetadataList } from '@/audit/api/types';
import { ArrowForwardRounded } from '@/constants/hardfin-standard-icons';
import { Box, Stack, Typography } from '@mui/material';

interface MetadataUpdate {
  id: string;
  label: string;
  oldValue: string | null;
  newValue: string | null;
}

interface Props {
  property: AssetMetadataList;
}

export function AuditAssetMetadataListLabel(props: Props) {
  const updates: MetadataUpdate[] = [];

  props.property.oldValue.forEach(({ id, value, type }) => {
    updates.push({ id, label: type.label, oldValue: value, newValue: null });
  });

  let noOpProps = 0;
  props.property.newValue.forEach(({ id, value, type }) => {
    const update = updates.find((update) => update.id === id);

    if (update === undefined) {
      if (value == null) {
        noOpProps++;
      }
      return updates.push({
        id,
        label: type.label,
        oldValue: null,
        newValue: value,
      });
    }

    update.newValue = value;
    if (update.oldValue === null && value == null) {
      noOpProps++;
    }
  });
  return (
    <Stack>
      <Typography component="span" variant="caption" fontWeight="bold">
        {props.property.name}:
      </Typography>
      {updates.length === noOpProps && (
        <Typography variant="caption" marginLeft={2}>
          No properties had changes
        </Typography>
      )}
      <Box
        display="grid"
        columnGap={0.5}
        gridTemplateColumns="repeat(4, min-content)"
      >
        {updates.map(({ id, label, oldValue, newValue }) => {
          if (oldValue === newValue) {
            return null;
          }

          const oldValueLabel = oldValue ?? 'None';
          const oldValueColor =
            oldValue === null ? 'text.secondary' : 'text.primary';

          const newValueLabel = newValue ?? 'None';
          const newValueColor =
            newValue === null ? 'text.secondary' : 'text.primary';

          return (
            <Typography
              key={id}
              variant="caption"
              component="span"
              display="grid"
              alignItems="center"
              gridColumn="span 4"
              gridTemplateColumns="subgrid"
              marginLeft={2}
            >
              <Typography
                component="span"
                variant="inherit"
                whiteSpace="nowrap"
              >
                {label}:
              </Typography>
              <Typography
                component="span"
                variant="inherit"
                whiteSpace="nowrap"
                color={oldValueColor}
              >
                {oldValueLabel}
              </Typography>
              <ArrowForwardRounded fontSize="inherit" />
              <Typography
                component="span"
                variant="inherit"
                whiteSpace="nowrap"
                color={newValueColor}
              >
                {newValueLabel}
              </Typography>
            </Typography>
          );
        })}
      </Box>
    </Stack>
  );
}
